import { useState } from "react";
import { FolderPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useProjeto } from "@/lib/projeto-context";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  Novo subprojeto (PL-005, 15/09)
 *
 *  O cliente pediu que o Gestor crie subprojetos dentro do projeto em que está.
 *  Um subprojeto é um projeto com cronograma e registos próprios, ligado ao pai
 *  (`projetos.projeto_pai_id`). Não passa por Administração → Projetos, que é só
 *  de administradores: `criar_subprojeto()` verifica a capacidade `subprojetos`
 *  do papel no projeto pai.
 *
 *  Quem cria entra como Gestor; os outros membros do pai entram pela gestão de
 *  utilizadores — não se copiam, porque um subprojeto existe muitas vezes para
 *  restringir quem vê o quê.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function NovoSubprojeto() {
  const { temCapacidade } = useAuth();
  const { projetoAtivo, recarregarProjetos, trocarProjeto } = useProjeto();
  const [aberto, setAberto] = useState(false);
  const [codigo, setCodigo] = useState("");
  const [nome, setNome] = useState("");
  const [inicio, setInicio] = useState("");
  const [goLive, setGoLive] = useState("");
  const [aGravar, setAGravar] = useState(false);

  if (!projetoAtivo || !temCapacidade("subprojetos")) return null;

  async function criar() {
    if (!codigo.trim() || !nome.trim()) {
      toast.error("O código e o nome são obrigatórios.");
      return;
    }
    setAGravar(true);
    try {
      const { data, error } = await supabase.rpc("criar_subprojeto", {
        _pai_id: projetoAtivo!.id,
        _codigo: codigo,
        _nome: nome,
        _data_inicio: inicio || (undefined as never),
        _data_go_live: goLive || (undefined as never),
      });
      if (error) throw error;
      toast.success(`Subprojeto ${codigo.trim().toUpperCase()} criado.`);
      setAberto(false);
      setCodigo("");
      setNome("");
      setInicio("");
      setGoLive("");
      await recarregarProjetos();
      // Abrir o subprojeto logo a seguir: quem o criou vai querer planeá-lo, e
      // ficar no pai obrigava a procurá-lo no seletor.
      if (data) await trocarProjeto(data as string);
    } catch (e: any) {
      toast.error(e?.message ?? "Não foi possível criar o subprojeto.");
    } finally {
      setAGravar(false);
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setAberto(true)}
        title={`Criar um subprojeto de ${projetoAtivo.codigo}`}
      >
        <FolderPlus className="size-4" />
        <span className="hidden md:inline">Subprojeto</span>
      </Button>

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo subprojeto de {projetoAtivo.codigo}</DialogTitle>
            <DialogDescription>
              Um projeto com cronograma e registos próprios, ligado a {projetoAtivo.nome}. Entra
              nele como Gestor; os outros membros são adicionados em Utilizadores.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="sub-codigo">Código</Label>
              <Input
                id="sub-codigo"
                value={codigo}
                placeholder={`${projetoAtivo.codigo}-01`}
                onChange={(e) => setCodigo(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="sub-nome">Nome</Label>
              <Input id="sub-nome" value={nome} onChange={(e) => setNome(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="sub-inicio">Início</Label>
                <Input id="sub-inicio" type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="sub-golive">Go-live</Label>
                <Input id="sub-golive" type="date" value={goLive} onChange={(e) => setGoLive(e.target.value)} />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAberto(false)} disabled={aGravar}>
              Cancelar
            </Button>
            <Button onClick={() => void criar()} disabled={aGravar}>
              Criar subprojeto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
